import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, isAdmin, clearAdminCache } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  isAdminUser: boolean;
  loadingAuth: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  validateSession: () => Promise<boolean>;
  saveScrollPosition: () => void;
  restoreScrollPosition: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('🏗️ AuthProvider组件初始化');
  const [user, setUser] = useState<User | null>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const loading = loadingAuth;
  
  console.log('🔄 AuthProvider状态:', { loadingAuth, loading, hasUser: !!user });

  useEffect(() => {
    console.log('⚡ AuthProvider useEffect触发');
    let isMounted = true;
    let initializationComplete = false;
    
    // 简化的初始化函数
    const initializeAuth = async () => {
      console.log('🔄 开始初始化认证状态...');
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.error('❌ 获取session时出错:', error);
        } else if (session?.user) {
          console.log('✅ 用户已登录:', session.user.email);
          // 打印JWT token信息
          printJwtToken(session);
          
          setUser(session.user);
          // 异步检查管理员权限
          isAdmin(session.user).then(adminStatus => {
            if (isMounted) {
              setIsAdminUser(adminStatus);
            }
          }).catch(() => {
            if (isMounted) {
              setIsAdminUser(false);
            }
          });
        } else {
          console.log('ℹ️ 用户未登录');
          setUser(null);
          setIsAdminUser(false);
        }
      } catch (error) {
        console.error('❌ 初始化认证状态时出错:', error);
        if (isMounted) {
          setUser(null);
          setIsAdminUser(false);
        }
      } finally {
        if (isMounted && !initializationComplete) {
          initializationComplete = true;
          setLoadingAuth(false);
          console.log('✅ 认证初始化完成');
        }
      }
    };

    // 强制超时保护
    const timeoutId = setTimeout(() => {
      if (isMounted && !initializationComplete) {
        console.log('⏰ 认证初始化超时，强制完成');
        initializationComplete = true;
        setLoadingAuth(false);
      }
    }, 2000); // 减少到2秒

    // 执行初始化
    initializeAuth();

    // 打印JWT token内容的辅助函数
    const printJwtToken = (session: any) => {
      if (session?.access_token) {
        try {
          const token = session.access_token;
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const payload = JSON.parse(jsonPayload);
          
          console.log('🔑 JWT Token 详细信息:');
          console.log('📋 完整 JWT Payload:', payload);
          console.log('👤 用户角色 (role):', payload.role || '未设置');
          console.log('📊 App Metadata:', payload.app_metadata || {});
          console.log('👥 User Metadata:', payload.user_metadata || {});
          console.log('⏰ Token 过期时间:', new Date(payload.exp * 1000).toLocaleString());
          
          if (payload.role) {
            console.log('✅ JWT 中包含 role 字段:', payload.role);
          } else {
            console.log('❌ JWT 中缺少 role 字段！');
          }
        } catch (error) {
          console.error('❌ 解析 JWT token 时出错:', error);
        }
      } else {
        console.log('❌ 没有找到 access_token');
      }
    };

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔄 认证状态变化:', event);
      
      if (!isMounted) return;
      
      clearAdminCache();
      
      if (session?.user) {
        console.log('✅ 用户登录成功:', session.user.email);
        // 打印JWT token信息
        printJwtToken(session);
        
        setUser(session.user);
        isAdmin(session.user).then(adminStatus => {
          if (isMounted) {
            setIsAdminUser(adminStatus);
          }
        }).catch(() => {
          if (isMounted) {
            setIsAdminUser(false);
          }
        });
      } else {
        setUser(null);
        setIsAdminUser(false);
      }
      
      // 确保加载状态结束
      if (!initializationComplete) {
        initializationComplete = true;
        setLoadingAuth(false);
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      // 忽略常见的认证相关错误，这些通常表示用户已经退出或session无效
      if (error) {
        const errorMessage = error.message?.toLowerCase() || '';
        const isAuthError = 
          errorMessage.includes('session') ||
          errorMessage.includes('expired') ||
          errorMessage.includes('invalid') ||
          errorMessage.includes('forbidden') ||
          errorMessage.includes('unauthorized') ||
          error.message === 'Auth session missing!';
        
        if (!isAuthError) {
          throw error;
        }
        // 对于认证错误，只记录日志但不抛出异常
        console.log('退出登录时的认证错误（已忽略）:', error.message);
      }
    } catch (error: any) {
      // 对于网络错误或其他非认证相关错误，记录但不抛出
      console.warn('退出登录时发生错误:', error);
    } finally {
      // 无论是否出错，都要确保本地状态被清理
      clearAdminCache();
    }
  };

  const validateSession = async (): Promise<boolean> => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.warn('获取session时出错:', sessionError);
        return false;
      }

      if (!session?.user) {
        // 没有session，如果当前状态认为已登录，则清除状态
        if (user) {
          setUser(null);
          setIsAdminUser(false);
          clearAdminCache();
        }
        return false;
      }

      // 验证session是否有效
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        console.warn('Session验证失败:', userError);
        // 清除无效session
        setUser(null);
        setIsAdminUser(false);
        clearAdminCache();
        await supabase.auth.signOut();
        return false;
      }

      // Session有效，更新用户状态（如果需要）
      if (!user || user.id !== currentUser.id) {
        setUser(currentUser);
        // 异步检查管理员权限，不阻塞验证流程
        isAdmin(currentUser).then(adminStatus => {
          setIsAdminUser(adminStatus);
        }).catch(error => {
          console.error('管理员权限检查失败:', error);
          setIsAdminUser(false);
        });
      }

      return true;
    } catch (error) {
      console.error('验证session时出错:', error);
      return false;
    }
  };

  const saveScrollPosition = () => {
    sessionStorage.setItem('authScrollPosition', window.scrollY.toString());
  };

  const restoreScrollPosition = () => {
    const scrollPosition = sessionStorage.getItem('authScrollPosition');
    if (scrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(scrollPosition));
        sessionStorage.removeItem('authScrollPosition');
      }, 100);
    }
  };

  const value = {
    user,
    isAdminUser,
    loadingAuth,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    validateSession,
    saveScrollPosition,
    restoreScrollPosition,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};